
import { Dispatcher } from '@fanfanlo';
import { dipspatcherPreventDefault, clearDispatcherPreventDefault } from '@fanfanlo';
import { MatrixClient } from 'matrix-js-sdk';
import { MatrixClientMethod } from './types';

// 事件名前缀
const EVENT_PREFIX = 'matrix.client.';

/**
 * 创建 MatrixClient 的代理
 * @param client 要代理的 MatrixClient 实例
 * @param dispatcher 事件分发器
 * @returns 代理后的 MatrixClient 实例
 */
export function createMatrixClientProxy(
  client: MatrixClient,
  dispatcher: Dispatcher
): MatrixClient {
  // 添加事件分发器的防抖处理
  let isSyncing = false;
  const syncHandlers = new Set<() => void>();

  /**
   * 包装方法调用
   */
  const wrapMethod = async <M extends MatrixClientMethod>(
    method: M,
    target: any,
    args: any[]
  ): Promise<any> => {
    // 将方法名转换为字符串进行比较
    const methodName = String(method);
    
    // 对 sync 方法进行特殊处理
    if (methodName === 'sync') {
      if (isSyncing) {
        // 如果已经在同步中，直接返回
        return target[method](...args);
      }
      isSyncing = true;
      try {
        const result = await target[method](...args);
        return result;
      } finally {
        isSyncing = false;
      }
    }

    // 派发 before 事件
    let beforeArgs = args;
    try {
      const event = await dispatchBeforeEvent(method, args);
      if (event !== dipspatcherPreventDefault) {
        beforeArgs = event;
      }
    } catch (error) {
      console.error(`Error in before event for ${String(method)}:`, error);
    }
    
    try {
      // 调用原始方法
      const result = await target[method](...beforeArgs);
      
      // 派发 after 事件
      await dispatchAfterEvent(method, beforeArgs, result);
      
      return result;
    } catch (error) {
      // 派发 error 事件
      await dispatchErrorEvent(method, beforeArgs, error);
      throw error;
    }
  };

  /**
   * 派发 before 事件
   */
  const dispatchBeforeEvent = async <M extends MatrixClientMethod>(
    method: M,
    args: any[]
  ): Promise<any[]> => {
    const eventType = `before:${EVENT_PREFIX}${String(method)}` as const;

    try {
      const event = {
        type: eventType,
        data: args,
        result: undefined,
        extra: { method }
      };

      const result = await dispatcher.dispatch(event);

      if (result === dipspatcherPreventDefault && Array.isArray(dipspatcherPreventDefault.arguments)) {
        clearDispatcherPreventDefault();
        return dipspatcherPreventDefault.arguments;
      }
      return args;
    } catch (error) {
      console.error(`Error in before event for ${String(method)}:`, error);
      return args;
    }
  };

  /**
   * 派发 after 事件
   */
  const dispatchAfterEvent = async <M extends MatrixClientMethod, T = any>(
    method: M,
    args: any[],
    result: T
  ): Promise<void> => {
    const eventType = `after:${EVENT_PREFIX}${String(method)}` as const;

    try {
      await dispatcher.dispatch({
        type: eventType,
        data: args,
        result,
        extra: { method }
      });
    } catch (error) {
      console.error(`Error in after event for ${String(method)}:`, error);
    }
  };

  /**
   * 派发 error 事件
   */
  const dispatchErrorEvent = async <M extends MatrixClientMethod>(
    method: M,
    args: any[],
    error: any
  ): Promise<void> => {
    const eventType = `error:${EVENT_PREFIX}${String(method)}` as const;

    try {
      await dispatcher.dispatch({
        type: eventType,
        data: args,
        error,
        extra: { method }
      });
    } catch (e) {
      console.error(`Error in error event for ${String(method)}:`, e);
    }
  };

  // 创建代理对象
  const handler: ProxyHandler<MatrixClient> = {
    get(target, prop) {
      if (typeof prop === 'string' && prop in target) {
        const value = (target as any)[prop];
        if (typeof value === 'function') {
          return (...args: any[]) => {
            return wrapMethod(prop as MatrixClientMethod, target, args);
          };
        }
        return value;
      }
      return undefined;
    }
  };

  return client;
  return new Proxy(client, handler);
}